import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Plus, Package, Search, RefreshCw, Inbox, ClipboardList, Users, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import QRScanner from '../components/QRScanner';

const STATUS_STYLES = {
  in_transit: 'bg-blue-100 text-blue-700',
  received: 'bg-amber-100 text-amber-700',
  processing: 'bg-purple-100 text-purple-700',
  closed: 'bg-gray-100 text-gray-600',
};

const ParcelsInward = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('parcels');
  const [parcels, setParcels] = useState([]);
  const [myQueue, setMyQueue] = useState([]);
  const [queueSummary, setQueueSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [config, setConfig] = useState({});
  const [form, setForm] = useState({
    sender_type: '', sender_name: '', sender_phone: '',
    courier_name: '', tracking_number: '', expected_items_summary: '', notes: '',
  });

  const canCreate = ['admin', 'inventory_dept'].includes(currentUser?.role);
  const [scannerOpen, setScannerOpen] = useState(false);

  const handleScan = async (decodedText) => {
    setScannerOpen(false);
    try {
      const res = await api.post('/parcels-inward/scan', { parcel_id: decodedText, action_type: 'lookup' });
      toast.success(`Parcel found: ${res.data.parcel_id}`);
      navigate(`/parcels-inward/${res.data.parcel_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Parcel not found');
    }
  };

  useEffect(() => {
    api.get('/parcels-inward/config').then(r => setConfig(r.data)).catch(() => {});
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pRes, qRes, sRes] = await Promise.all([
        api.get('/parcels-inward/parcels'),
        api.get('/parcels-inward/queue/my'),
        api.get('/parcels-inward/queue/summary'),
      ]);
      setParcels(pRes.data);
      setMyQueue(qRes.data);
      setQueueSummary(sRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.sender_name) {
      toast.error('Sender name is required');
      return;
    }
    try {
      const res = await api.post('/parcels-inward/parcels', form);
      toast.success(`Parcel ${res.data.parcel_id} created`);
      setCreateOpen(false);
      setForm({ sender_type: '', sender_name: '', sender_phone: '', courier_name: '', tracking_number: '', expected_items_summary: '', notes: '' });
      fetchData();
      navigate(`/parcels-inward/${res.data.parcel_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    }
  };

  const filteredParcels = parcels.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return p.parcel_id?.toLowerCase().includes(q) ||
      p.sender_name?.toLowerCase().includes(q) ||
      p.tracking_number?.toLowerCase().includes(q);
  });

  const deptLabels = config.dept_labels || {};
  const totalQueueItems = Object.values(queueSummary).reduce((a, b) => a + b, 0);

  const tabs = [
    { id: 'parcels', label: 'Parcels', icon: Package, count: parcels.length },
    { id: 'my-queue', label: 'My Queue', icon: Inbox, count: myQueue.length },
    { id: 'dept-summary', label: 'Departments', icon: Users, count: totalQueueItems },
  ];

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="parcels-inward-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Package className="w-5 h-5" /> Parcels Inward
              </h1>
              <p className="text-xs text-emerald-200">{parcels.length} parcels tracked</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="text-white hover:bg-emerald-700 h-8" onClick={() => setScannerOpen(true)} data-testid="scan-qr-btn">
              <ScanLine className="w-4 h-4 mr-1" /> Scan
            </Button>
            {canCreate && (
              <Button size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 h-8" onClick={() => setCreateOpen(true)} data-testid="create-parcel-btn">
                <Plus className="w-4 h-4 mr-1" /> New Parcel
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-white border-b">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
              tab === t.id ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500' : 'text-gray-500 hover:bg-gray-50'
            }`} data-testid={`tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
            {t.count > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold">{t.count}</span>}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Parcels Tab */}
        {tab === 'parcels' && (
          <div className="p-4 space-y-3">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input placeholder="Search parcel, sender..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-emerald-600" /></div>
            ) : filteredParcels.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No parcels found</p>
              </div>
            ) : filteredParcels.map(p => (
              <div key={p.parcel_id} onClick={() => navigate(`/parcels-inward/${p.parcel_id}`)}
                className="bg-white rounded-lg border p-4 cursor-pointer hover:shadow-md transition-shadow" data-testid={`parcel-${p.parcel_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-gray-900">{p.parcel_id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLES[p.status] || 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{p.sender_name}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                      <span>{p.sender_type}</span>
                      {p.tracking_number && <span>#{p.tracking_number}</span>}
                      <span>{p.items_count} item{p.items_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">{new Date(p.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* My Queue Tab */}
        {tab === 'my-queue' && (
          <div className="p-4 space-y-3" data-testid="my-queue">
            {myQueue.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Inbox className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Nothing in your queue</p>
              </div>
            ) : myQueue.map(item => (
              <div key={item.item_id} onClick={() => navigate(`/parcels-inward/${item.parcel_id}`)}
                className="bg-white rounded-lg border p-4 cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-gray-900">{item.item_id}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700">{item.item_type_label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLES[item.status] || 'bg-gray-100 text-gray-600'}`}>{item.status}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Dept: {deptLabels[item.current_department] || item.current_department}</p>
                  </div>
                  <div className="text-right text-xs text-gray-400">
                    {item.gross_weight && <p>{item.gross_weight}g</p>}
                    {item.carats && <p>{item.carats}ct</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Department Summary Tab */}
        {tab === 'dept-summary' && (
          <div className="p-4 space-y-3" data-testid="dept-summary">
            {Object.keys(queueSummary).length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No active items across departments</p>
              </div>
            ) : Object.entries(queueSummary).sort((a, b) => b[1] - a[1]).map(([dept, count]) => (
              <div key={dept} onClick={() => navigate(`/parcels-inward/dept/${dept}`)}
                className="bg-white rounded-lg border p-4 cursor-pointer hover:shadow-md transition-shadow flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-semibold text-sm text-gray-900">{deptLabels[dept] || dept}</span>
                </div>
                <span className="text-lg font-bold text-emerald-700">{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Parcel Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-parcel-dialog">
          <DialogHeader><DialogTitle>Receive New Parcel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Sender Type</Label>
                <Select value={form.sender_type} onValueChange={v => setForm(f => ({...f, sender_type: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {(config.sender_types || []).map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Sender Name *</Label>
                <Input value={form.sender_name} onChange={e => setForm(f => ({...f, sender_name: e.target.value}))} placeholder="Job worker / supplier" data-testid="sender-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Courier</Label>
                <Input value={form.courier_name} onChange={e => setForm(f => ({...f, courier_name: e.target.value}))} placeholder="Courier name" />
              </div>
              <div className="space-y-1">
                <Label>Tracking #</Label>
                <Input value={form.tracking_number} onChange={e => setForm(f => ({...f, tracking_number: e.target.value}))} placeholder="Tracking number" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Expected Items Summary</Label>
              <textarea value={form.expected_items_summary} onChange={e => setForm(f => ({...f, expected_items_summary: e.target.value}))} rows={2} className="w-full px-3 py-2 border rounded-md text-sm" placeholder="Brief description of expected contents..." />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Any notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#075E54] hover:bg-[#064E46]" data-testid="save-parcel-btn">Create Parcel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Overlay */}
      {scannerOpen && (
        <QRScanner onScan={handleScan} onClose={() => setScannerOpen(false)} />
      )}
    </div>
  );
};

export default ParcelsInward;
